import { CheckCircle2 } from "lucide-react";
import type { FinalStepSchema } from "@schema-types/types/JourneySchema";

type ThemeWindow = {
  __THEME__?: {
    organisationName?: string;
    logoUrl?: string;
  };
};

const closingLines = [
  "We have received your details. We will begin work on your case and will be in touch with the next steps.",
  "You can safely close this page. If anything changes or you have questions, we will contact you using the details you provided.",
];

const completionChecklistItems = [
  "Your submission has been received and saved securely—you do not need to send it again.",
  "Our team will review what you shared and contact you about what happens next, using the details you gave us.",
  "You can close this window whenever you are ready; everything you completed here is already with us.",
];

export const FinalStep = ({ title, description }: Pick<FinalStepSchema, "title" | "description">) => {
  const theme = typeof window !== "undefined" ? ((window as unknown as ThemeWindow).__THEME__ ?? {}) : {};
  const heading = title?.trim() || "Thank you — you're all set";
  const customDescription = description?.trim();

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center text-center">
        {theme.logoUrl ? (
          <img src={theme.logoUrl} alt="" className="mb-6 h-12 max-w-[200px] object-contain" />
        ) : null}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{heading}</h1>
        {customDescription ? (
          <p className="mt-4 max-w-xl text-base text-muted-foreground">{customDescription}</p>
        ) : (
          <div className="mt-4 max-w-xl space-y-3 text-base text-muted-foreground">
            {closingLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-6 sm:p-8">
        <p className="mb-4 text-center text-sm font-medium text-foreground">What this means for you</p>
        <ul className="mx-auto max-w-lg space-y-4 text-left text-sm text-muted-foreground">
          {completionChecklistItems.map((line) => (
            <li key={line} className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
